import {
  Box,
  Checkbox,
  IconButton,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
} from '@mui/material';
import React, { useState } from 'react';
import { ComparisonEventsListRow } from './ComparisonEventsListRow';
import type { Comparison, ComparisonItem } from '../../../../../types/BaselineMeasurements';
import { ComparisonItemType } from '../../../../../types/BaselineMeasurements';
import type { Connection } from '../../../../../types';
import type { PaginationProps } from 'app/components';
import { Pagination } from 'app/components';
import Skeleton from '@mui/material/Skeleton';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import { TranslationKeys } from '../../../../../translations';
import { useIntl } from 'react-intl';

type ComparisonEventsListProps = {
  connectionId: Connection['connectionId'] | undefined;
  comparison: Comparison | undefined;
  items?: ComparisonItem[];
  selectedItems: Set<string>;
  onSelectItem: (selected: Set<string>) => void;
  onSelectAllItems: (selectAll: boolean) => void;
  paginationProps: PaginationProps;
  loading: boolean;
};

export const ComparisonEventsList = ({
  connectionId,
  comparison,
  items,
  selectedItems,
  onSelectItem,
  onSelectAllItems,
  paginationProps,
  loading,
}: ComparisonEventsListProps) => {
  const intl = useIntl();
  const selectableItems = items?.filter(item => item.itemType !== ComparisonItemType.Unknown) || [];
  const [allOpen, setAllOpen] = useState(false);
  const isAllSelected =
    selectableItems.length > 0 && selectableItems.every(item => selectedItems.has(item.comparisonItemId));
  const onSelectItemHandler = React.useCallback(
    (id: string | null) => () => {
      if (!id) {
        return;
      }
      const isSelected = selectedItems.has(id);

      if (isSelected) {
        selectedItems.delete(id);
      } else {
        selectedItems.add(id);
      }
      onSelectItem(new Set(selectedItems));
    },
    [onSelectItem, selectedItems],
  );

  const handleSelectAll = (event: React.ChangeEvent<HTMLInputElement>) => {
    onSelectAllItems(event.target.checked);
  };

  return (
    <Paper sx={{ borderRadius: 2 }}>
      <TableContainer sx={{ position: 'relative', zIndex: 1 }}>
        <Table stickyHeader>
          <TableHead>
            <TableRow>
              <TableCell padding="checkbox">
                <Checkbox indeterminate={false} checked={isAllSelected} color="primary" onChange={handleSelectAll} />
              </TableCell>
              <TableCell padding="checkbox" sx={{ py: 0.5 }}>
                <IconButton aria-label="expand row" size="small" onClick={() => setAllOpen(!allOpen)}>
                  {allOpen ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
                </IconButton>
              </TableCell>
              <TableCell sx={{ fontWeight: 'bold' }}>
                {intl.formatMessage({ id: TranslationKeys['baseline_comparison_items_list_header_event-type'] })}
              </TableCell>
              <TableCell sx={{ fontWeight: 'bold' }}>
                {intl.formatMessage({ id: TranslationKeys['baseline_comparison_items_list_header_employee'] })}
              </TableCell>
              <TableCell sx={{ fontWeight: 'bold' }}>
                {intl.formatMessage({ id: TranslationKeys['baseline_comparison_items_list_header_differences'] })}
              </TableCell>
              <TableCell sx={{ fontWeight: 'bold' }}>
                {intl.formatMessage({ id: TranslationKeys['baseline_comparison_items_list_header_actions'] })}
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {loading &&
              new Array(10).fill(1).map((value, index) => (
                <TableRow key={index}>
                  <TableCell>
                    <Skeleton />
                  </TableCell>
                  <TableCell>
                    <Skeleton />
                  </TableCell>
                  <TableCell>
                    <Skeleton />
                  </TableCell>
                  <TableCell>
                    <Skeleton />
                  </TableCell>
                  <TableCell>
                    <Skeleton />
                  </TableCell>
                  <TableCell>
                    <Skeleton />
                  </TableCell>
                </TableRow>
              ))}
            {!loading &&
              items &&
              items.map((item, index) => (
                <ComparisonEventsListRow
                  key={item.comparisonItemId || index}
                  connectionId={connectionId}
                  comparison={comparison}
                  item={item}
                  itemOpen={allOpen}
                  onSelectItem={onSelectItemHandler(item.comparisonItemId)}
                  selected={selectedItems.has(item.comparisonItemId)}
                />
              ))}
          </TableBody>
        </Table>
      </TableContainer>

      {!!paginationProps && (
        <Box>
          <Pagination {...paginationProps} />
        </Box>
      )}
    </Paper>
  );
};
